import { db } from "../../../../services/firebase";
import { collection, query, where, orderBy, getDocs, limit } from "firebase/firestore";
import { COLLECTIONS } from "../../../../services/collections";
import { FINANCIAL_STATUS } from "../../../../constants/financial";

/**
 * fetchCashFlowData
 * Service Layer: Dispara e sincroniza queries simultâneas isolando a sujeira de DB do Hook de interface.
 * 
 * OTIMIZAÇÃO: Todas as queries usam `limit` para evitar leituras explosivas.
 * Atrasados antigos são limitados aos últimos 90 dias.
 */

const MAX_RECORDS_PER_QUERY = 500;

const getDateDaysAgo = (days) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().split("T")[0];
};

export const fetchCashFlowData = async (idTenant, startDate, endDate) => {
    // 1. Busca contas bancárias
    const snapBanks = await getDocs(collection(db, "tenants", idTenant, COLLECTIONS.BANK_ACCOUNTS));
    const bankAccounts = [];
    snapBanks.forEach(doc => bankAccounts.push({ id: doc.id, ...doc.data() }));

    const recordsRef = collection(db, "tenants", idTenant, COLLECTIONS.FINANCIAL_RECORDS);

    // 2a. Busca PENDING/OVERDUE com dueDate no período
    const qPending = query(
        recordsRef,
        where("status", "in", [FINANCIAL_STATUS.PENDING, FINANCIAL_STATUS.OVERDUE]),
        where("dueDate", ">=", startDate),
        where("dueDate", "<=", endDate),
        orderBy("dueDate", "asc"),
        limit(MAX_RECORDS_PER_QUERY)
    );

    // 2b. Busca PAID com paymentDate no período
    const qPaid = query(
        recordsRef,
        where("status", "==", FINANCIAL_STATUS.PAID),
        where("paymentDate", ">=", startDate),
        where("paymentDate", "<=", endDate),
        orderBy("paymentDate", "asc"),
        limit(MAX_RECORDS_PER_QUERY)
    );

    // 2c. Busca atrasados anteriores ao período (máx 90 dias para trás)
    const overdueFloor = getDateDaysAgo(90);
    const qOverdueOld = query(
        recordsRef,
        where("status", "in", [FINANCIAL_STATUS.PENDING, FINANCIAL_STATUS.OVERDUE]),
        where("dueDate", ">=", overdueFloor),
        where("dueDate", "<", startDate),
        orderBy("dueDate", "asc"),
        limit(MAX_RECORDS_PER_QUERY)
    );

    const [snapPending, snapPaid, snapOverdueOld] = await Promise.all([
        getDocs(qPending),
        getDocs(qPaid),
        getDocs(qOverdueOld),
    ]);

    const allRecords = new Map();

    // Consolidar resultados (Map evita duplicatas considerando atrasados velhos que colidem)
    const addSnap = (snap) => {
        snap.forEach(doc => {
            if (!allRecords.has(doc.id)) {
                allRecords.set(doc.id, { id: doc.id, ...doc.data() });
            }
        });
    };

    addSnap(snapPending);
    addSnap(snapPaid);
    addSnap(snapOverdueOld);

    return {
        bankAccounts,
        records: Array.from(allRecords.values())
    };
};
